import { useAppEventBusContext } from '@/context/eventBusContext';
import { useWebSocket } from '@/context/websocketContext';
import { SubscribeEvent } from '@/interface/event';
import { useEffect, useRef } from 'react';
import { useSignalStore } from '../store';
import { useNoticeHandler } from './useNoticeHandler';
import BigNumber from 'bignumber.js';
import { useBoomWallet } from '@boomeme/wallet-sdk';

export const useWsSignalToken = (params: string[]) => {
  const { data: wsData, isConnected, sendMessage } = useWebSocket();
  const { publish } = useAppEventBusContext();
  const { chain } = useBoomWallet();
  const { bscSignalTradingSetting, solSignalTradingSetting, filterConfig } = useSignalStore();
  const { showNotification } = useNoticeHandler();
  const prevParamsRef = useRef<string[]>([]);
  const subscribedAddressesRef = useRef<Set<string>>(new Set());
  const targetChainTradingSetting = chain == 'bsc' ? bscSignalTradingSetting : solSignalTradingSetting;
  const { alert } = targetChainTradingSetting;
  useEffect(() => {
    if (!isConnected) return () => {};

    sendMessage('subscribeSignal');

    return () => sendMessage('unsubscribeSignal');
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;

    const newAddresses = params.filter((addr) => !prevParamsRef.current.includes(addr));

    if (newAddresses.length > 0) {
      sendMessage('subscribeSignalToken', { tokenAddresses: newAddresses });

      newAddresses.forEach((addr) => subscribedAddressesRef.current.add(addr));
    }

    prevParamsRef.current = [...params];
  }, [isConnected, params]);

  useEffect(() => {
    return () => {
      if (isConnected && subscribedAddressesRef.current.size > 0) {
        const addresses = Array.from(subscribedAddressesRef.current);
        sendMessage('unsubscribeSignalToken', { tokenAddresses: addresses });
        subscribedAddressesRef.current.clear();
      }
    };
  }, [isConnected, subscribedAddressesRef]);

  useEffect(() => {
    if (!wsData?.data) return;

    if (wsData.event === 'alert') {
      const signal = wsData.data;
      publish(SubscribeEvent.Update_Signal, wsData.data);
      // 检查信号是否满足所有过滤条件
      const meetsFilterCriteria = () => {
        // 检查是否应用了任何筛选条件
        const hasNoFilters =
          !filterConfig.alertTimes.start &&
          !filterConfig.alertTimes.end &&
          !filterConfig.mc.start &&
          !filterConfig.mc.end &&
          !filterConfig.liquidity.start &&
          !filterConfig.liquidity.end &&
          !filterConfig.holders.start &&
          !filterConfig.holders.end;

        // 如果没有应用任何筛选条件，直接返回true
        if (hasNoFilters) return true;

        // 提醒次数筛选
        if (filterConfig.alertTimes.start && signal.pushTimes < Number(filterConfig.alertTimes.start)) return false;
        if (filterConfig.alertTimes.end && signal.pushTimes > Number(filterConfig.alertTimes.end)) return false;

        // 计算市值
        const mc = BigNumber(signal.alert.price).times(signal.totalSupply).div(1000).toString();

        // 市值筛选
        if (filterConfig.mc.start && Number(mc) < Number(filterConfig.mc.start)) return false;
        if (filterConfig.mc.end && Number(mc) > Number(filterConfig.mc.end)) return false;

        // 流动性筛选
        if (filterConfig.liquidity.start && Number(signal.alert.liquidity) < Number(filterConfig.liquidity.start)) return false;
        if (filterConfig.liquidity.end && Number(signal.alert.liquidity) > Number(filterConfig.liquidity.end)) return false;

        // 持有人数筛选
        if (filterConfig.holders.start && signal.alert.holders < Number(filterConfig.holders.start)) return false;
        if (filterConfig.holders.end && signal.alert.holders > Number(filterConfig.holders.end)) return false;

        return true;
      };

      // 只有满足所有条件的信号才会更新和提醒
      if (meetsFilterCriteria()) {
        alert && showNotification();
      }
    } else if (wsData.event === 'signalTokenUpdate') {
      const { data } = wsData;

      if (data.highestPrice !== undefined) {
        publish(SubscribeEvent.Update_Signal_Price, data);
      }

      if (data.liquidity !== undefined) {
        publish(SubscribeEvent.Update_Signal_Liquidity, data);
      }

      if (data.holders !== undefined) {
        publish(SubscribeEvent.Update_Signal_Holders, data);
      }
    }
  }, [wsData, publish, alert, filterConfig]);
};
